import type { FC, FormEvent, ReactNode } from 'react'
import type { FollowUp } from '@/lib/clinic/types'
import type { FollowUpFormValues } from '@/components/follow-ups/follow-up-fields'

import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { CalendarPlus, Clock3 } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { formatDate, formatDateTime } from '@/lib/clinic/datetime'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Field from '@/components/common/field'
import PatientAvatar from '@/components/common/patient-avatar'
import FollowUpFields, { createFollowUpValues } from '@/components/follow-ups/follow-up-fields'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

interface FollowUpDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    patientId: string | null
    consultationId: string | null
    followUpId: string | null
    onSaved?: (followUp: FollowUp) => void
}

interface FollowUpBodyProps extends Omit<FollowUpDialogProps, 'open' | 'onOpenChange'> {
    onClose: () => void
}

const FollowUpBody: FC<FollowUpBodyProps> = ({ patientId, consultationId, followUpId, onSaved, onClose }): ReactNode => {
    const { index, actions, today } = useClinic()
    const patient = patientId ? index.patientsById.get(patientId) ?? null : null
    const existing = followUpId ? index.followUpsById.get(followUpId) ?? null : null
    const consultations = patient ? index.consultationsByPatient.get(patient.id) ?? [] : []

    const [values, setValues] = useState<FollowUpFormValues>(() => {
        if (existing) {
            return {
                consultationId: existing.consultationId,
                treatment: existing.treatment,
                dueDate: existing.dueDate,
                notes: existing.notes
            }
        }

        const preferred = consultationId ? index.consultationsById.get(consultationId) ?? null : null

        return createFollowUpValues(preferred ?? consultations[0] ?? null)
    })

    const [reason, setReason] = useState('')
    const [dueDateError, setDueDateError] = useState<string | null>(null)
    const savingRef = useRef(false)

    if (!patient) {
        return (
            <div className='p-6'>
                <DialogTitle>Patient not found</DialogTitle>

                <DialogDescription className='mt-2'>
                    This patient record is no longer available.
                </DialogDescription>
            </div>
        )
    }

    const dateChanged = Boolean(existing && values.dueDate && values.dueDate !== existing.dueDate)

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!values.dueDate) {
            setDueDateError('Choose the returning date.')
            return
        }

        if (savingRef.current) return
        savingRef.current = true

        const input = {
            consultationId: values.consultationId,
            treatment: values.treatment,
            dueDate: values.dueDate,
            notes: values.notes
        }

        if (existing) {
            const updated = actions.updateFollowUp(existing.id, input, reason)

            if (updated) {
                toast.success('Follow-up updated', { description: `${patient.name} · ${formatDate(updated.dueDate)}` })
                onSaved?.(updated)
            }
        }
        else {
            const created = actions.createFollowUp(patient.id, input)

            toast.success(`Follow-up set for ${formatDate(created.dueDate)}`, { description: patient.name })
            onSaved?.(created)
        }

        onClose()
    }

    return (
        <>
            <div className='flex items-start gap-3 border-b px-5 py-4 pr-12'>
                <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground'>
                    <CalendarPlus className='size-5' />
                </span>

                <div className='flex min-w-0 flex-col gap-1'>
                    <DialogTitle className='text-lg font-semibold'>
                        {existing ? 'Edit follow-up' : 'Add follow-up'}
                    </DialogTitle>

                    <DialogDescription className='flex items-center gap-2'>
                        <PatientAvatar name={patient.name} seed={patient.id} size='sm' className='size-5 text-[9px]' />
                        <span className='truncate'>
                            {patient.name} · {patient.code}
                        </span>
                    </DialogDescription>
                </div>
            </div>

            <form id='follow-up-form' noValidate onSubmit={handleSubmit} className='flex-1 overflow-y-auto px-5 py-5'>
                <FollowUpFields
                    values={values}
                    onChange={next => {
                        setValues(next)
                        if (next.dueDate) setDueDateError(null)
                    }}
                    consultations={consultations}
                    today={today}
                    dueDateError={dueDateError}
                    idPrefix='follow-up-dialog'
                />

                {dateChanged && (
                    <Field label='Reason for changing the date' htmlFor='follow-up-reason' hint='Optional. Saved in the change history.' className='mt-5'>
                        <Input
                            id='follow-up-reason'
                            value={reason}
                            onChange={event => setReason(event.target.value)}
                            placeholder='e.g. Patient requested a later date'
                        />
                    </Field>
                )}

                <p className='mt-5 flex items-center gap-1.5 text-xs text-muted-foreground'>
                    <Clock3 className='size-3.5' />
                    {existing
                        ? `Created ${formatDateTime(existing.createdAt)}. The original time is kept.`
                        : 'The date and time this follow-up is created are saved automatically.'}
                </p>
            </form>

            <div className='flex flex-col-reverse gap-2 border-t bg-muted/40 px-5 py-3.5 sm:flex-row sm:justify-end'>
                <Button type='button' variant='ghost' onClick={onClose}>
                    Cancel
                </Button>

                <Button type='submit' form='follow-up-form' size='lg'>
                    {existing ? 'Save changes' : 'Save follow-up'}
                </Button>
            </div>
        </>
    )
}

const FollowUpDialog: FC<FollowUpDialogProps> = ({ open, onOpenChange, ...props }): ReactNode => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg'>
                <FollowUpBody onClose={() => onOpenChange(false)} {...props} />
            </DialogContent>
        </Dialog>
    )
}

export default FollowUpDialog
